import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Grid, Divider, Header, Form, Checkbox } from 'semantic-ui-react'
import { Nullable } from '~/types'
import { CheckClimaEl } from '../../../style'
import { StyledGrid, CustomRow, StyledSegment, HeaderField, StyledOtherMilkWrapper } from '../style'
import { FormFields } from '~/components/FormFields'
import { useFormContext, useWatch } from 'react-hook-form'
import { formatNumber, handleErrors, splitEvery } from '~/utils/helpers'
import InfoPopup from '~/components/InfoPopup'
import TableComponent from '~/components/TableComponent'
import PreviewModal from './PreviewModal'
import { useParams } from 'react-router-dom'
import ClimaCheckService, { ClimaCheckOptionsType } from '~/api/services/climaCheck'
import LinkPopup from '../LinkPopup'

// svgs
import MilkProductIcon from '@assets/svgs/milk-products.svg'
import MilkFatProteinIcon from '@assets/svgs/milk-fat-protein.svg'
import EuroCoin from '@assets/svgs/euro-coin.svg'
import AdressBookIcon from '@assets/svgs/contact-book.svg'
import ProfitIcon from '@assets/svgs/profit.svg'
import ChecmicalReport from '@assets/svgs/chemical-report.svg'
import { StyledGridColumns, StyledGridRow } from '~/pages/Users/User/Show/style'

interface Props {
  options: Nullable<ClimaCheckOptionsType>
  setOptions: (options: ClimaCheckOptionsType) => void
}

const FirstStep: React.FC<Props> = (props) => {
  const { options, setOptions } = props
  const { control, setValue, getValues } = useFormContext()
  const { id, farmerId } = useParams()
  const { t } = useTranslation()

  const billingData = [
    {
      label: 'Abgelieferte Milchmenge (in kg)',
      icon: MilkProductIcon,
      key: 'totalMilkAmount',
      min: 1000,
      max: 100000000,
      suffix: ' kg'
    },
    {
      label: t('Milk price net including surcharges (in € cents/kg)'),
      icon: ProfitIcon,
      decimalScale: 2,
      min: 20,
      max: 90,
      key: 'milkPrice',
      suffix: ' Ct/kg'
    },
    {
      label: t('Protein content (in%)'),
      key: 'protein',
      decimalScale: 2,
      min: 2,
      max: 6,
      icon: MilkFatProteinIcon,
      suffix: ' %'
    },
    {
      label: t('Fat content (in%)'),
      icon: MilkFatProteinIcon,
      key: 'fat',
      min: 3,
      max: 7,
      decimalScale: 2,
      suffix: ' %'
    },
    {
      label: t('Urea content (in ppm)'),
      icon: ChecmicalReport,
      key: 'ureaContent',
      decimalScale: 2,
      suffix: ' ppm'
    }
  ]

  const [isPreviewModal, setIsPreviewModal] = useState(false)

  const watchSellOtherMilkSelect = useWatch({ control, name: 'sellOtherMilkSelected' })
  const watchOtherMilk = useWatch({ control, name: 'otherMilkAmount' })
  const watchTotalAmount = useWatch({ control, name: 'totalMilkAmount' })
  const watchNaturalMilkAMount = useWatch({ control, name: 'naturalMilkAmount' })

  useEffect(() => {
    if (getValues('otherMilkAmount')) {
      setValue('sellOtherMilkSelected', true)
    }
    if (options.filePreview) {
      setIsPreviewModal(true)
    }
  }, [])

  useEffect(() => {
    if (!watchSellOtherMilkSelect) {
      setValue('otherMilkAmount', '')
    }
  }, [watchSellOtherMilkSelect])

  function renderFarmerInfo() {
    return <HeaderField>
      <Header className='header' style={{ marginBottom: 5 }}>
        <img style={{ width: '2.5em' }} src={AdressBookIcon} />
        {t('Dairy master data')}
      </Header>
      <CheckClimaEl.StyledDivider />
      <StyledSegment>
        <StyledGrid columns={2} stackable>
          <Divider vertical style={{ margin: 0 }} />
          <Grid.Column>
            <Grid columns={2} stackable>
              <CustomRow>
                <Grid.Column>{t('Supplier number')}:</Grid.Column>
                <StyledGridColumns>{options?.farmer?.supplierNo || '-'}</StyledGridColumns>
              </CustomRow>
              <StyledGridRow>
                <Grid.Column>{t('Firm name')}:</Grid.Column>
                <StyledGridColumns>{options?.farmer?.companyName || '-'}</StyledGridColumns>
              </StyledGridRow>
              <StyledGridRow>
                <Grid.Column>{t('Taxation')}:</Grid.Column>
                <StyledGridColumns>{options?.farmer?.saleTaxLabel || '-'}</StyledGridColumns>
              </StyledGridRow>
            </Grid>
          </Grid.Column>
          <Grid.Column style={{ paddingBottom: '1em' }}>
            <Grid columns={2} stackable style={{ marginLeft: 5 }}>
              <StyledGridRow>
                <Grid.Column className='left-column'>{t('Street & house number')}:</Grid.Column>
                <StyledGridColumns>{`${options?.farmer?.streetName || '-'}, ${options?.farmer?.streetNumber || '-'}`}</StyledGridColumns>
              </StyledGridRow>
              <StyledGridRow>
                <Grid.Column className='left-column'>{t('Postal code & City')}:</Grid.Column>
                <StyledGridColumns>{`${options?.farmer?.zip || '-'} ${options?.farmer?.city || '-'}`}</StyledGridColumns>
              </StyledGridRow>
              <StyledGridRow>
                <Grid.Column className='left-column'>{t('Federal state (Country)')}:</Grid.Column>
                <StyledGridColumns>{`${options.farmer?.stateName || '-'} (${options?.farmer?.label || '-'})`}</StyledGridColumns>
              </StyledGridRow>
            </Grid>
          </Grid.Column>
        </StyledGrid>
      </StyledSegment>
    </HeaderField>
  }

  function renderInputs() {
    return <CheckClimaEl.BillingWrapper>
      <Form.Group widths={'equal'} style={{ width: '100%', flexWrap: 'wrap', columnGap: '20px', rowGap: 10 }}>
        {billingData.map(billing => {
          const hideInput = options?.useMilkPrice === 0 && billing.key === 'milkPrice'
          return !hideInput && <FormFields.Input
            name={billing.key}
            className='bill-input'
            key={billing.key}
            typeInput={'controled'}
            allowDecimals={Boolean(billing?.decimalScale)}
            noErrorMessage
            withoutAsterisk
            decimalScale={billing?.decimalScale}
            control={control}
            suffix={billing.suffix}
            placeholder={billing.label}
            styleControlled={{ width: options?.useMilkPrice === 0 ? 250 : 200, marginLeft: 45 }}
            label={<div className='billing'>
              <img style={{ width: '2.5em' }} src={billing.icon} />
              <div className='column-billing'>
                <div>{billing.label}</div>
              </div>
            </div>}
            rules={{
              required: true,
              validate: (value) => {
                const transformValue = typeof (value) === 'string' ? parseFloat(value.replace(',', '.')) : value
                if (transformValue < billing.min || transformValue > billing.max) {
                  return false
                }
                return true
              }
            }}
          />
        })}
      </Form.Group>
    </CheckClimaEl.BillingWrapper>
  }

  function renderDairyDataTables(tableColumnHeader) {
    const dateColumn = {
      name: t('Month'),
      key: 'month',
      type: 'date',
      format: 'MMM YYYY'
    }
    const amountsSplit = splitEvery(options?.farmer?.amounts, 4)
    return <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
      {amountsSplit.map(amountSplit => {
        return <TableComponent
          key={`amountSplit-${Math.random()}`}
          tableColumnHeaders={[dateColumn, tableColumnHeader]}
          rows={amountSplit}
          total={amountSplit?.length}
          removePagination
        />
      })}
    </div>
  }

  function displayAsLabels() {
    return <HeaderField>
      <StyledSegment>
        <StyledGrid columns={2} stackable>
          <Divider vertical style={{ margin: 0 }} />
          <Grid.Column>
            <Grid columns={2} stackable>
              <CustomRow>
                <Grid.Column>
                  <div style={{ display: 'flex', alignItems: 'center' }}>
                    <img style={{ width: '2em', marginRight: 10 }} src={MilkProductIcon} />
                    <div className='column'>
                      <div>{'Abgelieferte Milchmenge (in kg)'}:</div>
                    </div>
                  </div>
                </Grid.Column>
                <StyledGridColumns style={{ marginTop: 10, display: 'flex' }}>
                  {`${formatNumber(options?.farmer?.totalMilkAmount, 0)} kg` || '-'}
                  <InfoPopup
                    content={renderDairyDataTables({ name: 'Abgelieferte Milchmenge', key: 'amount', type: 'number-whole', amountUnit: 'kg' })}
                    styleIcon={{ fontSize: '1.3em', marginLeft: 5 }}
                    position={'top center'}
                  />
                </StyledGridColumns>
              </CustomRow>
              {options?.useMilkPrice === 1 && <CustomRow>
                <Grid.Column>
                  <div style={{ display: 'flex', alignItems: 'center' }}>
                    <img style={{ width: '2em', marginRight: 10 }} src={ProfitIcon} />
                    <div className='column'>
                      <div>{t('Milk price net including surcharges (in cents/kg)')}:</div>
                    </div>
                  </div>
                </Grid.Column>
                <StyledGridColumns style={{ marginTop: 5, display: 'flex' }}>
                  {`${formatNumber(options?.farmer?.milkPrice, 2)} Ct/kg` || '-'}
                  <InfoPopup
                    content={renderDairyDataTables({
                      name: t('Net milk price including surcharges'),
                      key: 'netPrice',
                      type: 'value-with-unit',
                      decimalScale: 2,
                      amountUnit: '€ Ct/kg'
                    })}
                    styleIcon={{ fontSize: '1.3em', marginLeft: 5 }}
                    position={'top center'}
                  />
                </StyledGridColumns>
              </CustomRow>}
            </Grid>
          </Grid.Column>
          <Grid.Column>
            <Grid columns={2} stackable style={{ marginLeft: 5 }}>
              <CustomRow>
                <Grid.Column>
                  <div style={{ display: 'flex', alignItems: 'center' }}>
                    <img style={{ width: '2em', marginRight: 10 }} src={MilkFatProteinIcon} />
                    <div className='column'>
                      <div>{t('Protein content of the milk sold (in%)')}:</div>
                    </div>
                  </div>
                </Grid.Column>
                <StyledGridColumns style={{ marginTop: 5, display: 'flex' }}>
                  {`${formatNumber(options?.farmer?.protein, 2)} %` || '-'}
                  <InfoPopup
                    content={renderDairyDataTables({
                      name: t('Protein content of milk sold'),
                      key: 'protein',
                      type: 'value-with-unit',
                      decimalScale: 2,
                      amountUnit: '%'
                    })}
                    styleIcon={{ fontSize: '1.3em', marginLeft: 5 }}
                    position={'top center'}
                  />
                </StyledGridColumns>
              </CustomRow>
              <CustomRow>
                <Grid.Column>
                  <div style={{ display: 'flex', alignItems: 'center' }}>
                    <img style={{ width: '2em', marginRight: 10 }} src={MilkFatProteinIcon} />
                    <div className='column'>
                      <div>{t('Fat content of the milk sold (in%)')}:</div>
                    </div>
                  </div></Grid.Column>
                <StyledGridColumns style={{ marginTop: 5, display: 'flex' }}>
                  {`${formatNumber(options?.farmer?.fat, 2)} %` || '-'}
                  <InfoPopup
                    content={renderDairyDataTables({
                      name: t('Fat content of milk sold'),
                      key: 'fat',
                      type: 'value-with-unit',
                      decimalScale: 2,
                      amountUnit: '%'
                    })}
                    styleIcon={{ fontSize: '1.3em', marginLeft: 5 }}
                    position={'top center'}
                  />
                </StyledGridColumns>
              </CustomRow>
              <CustomRow>
                <Grid.Column>
                  <div style={{ display: 'flex', alignItems: 'center' }}>
                    <img style={{ width: '2em', marginRight: 10 }} src={MilkFatProteinIcon} />
                    <div className='column'>
                      <div>{t('Urea content (in ppm)')}:</div>
                    </div>
                  </div></Grid.Column>
                <StyledGridColumns style={{ marginTop: 5, display: 'flex' }}>
                  {`${formatNumber(options?.farmer?.ureaContent, 2)} ppm` || '-'}
                  <InfoPopup
                    content={renderDairyDataTables({
                      name: t('Urea content'),
                      key: 'ureaContent',
                      type: 'value-with-unit',
                      decimalScale: 2,
                      amountUnit: ' ppm'
                    })}
                    styleIcon={{ fontSize: '1.3em', marginLeft: 5 }}
                    position={'top center'}
                  />
                </StyledGridColumns>
              </CustomRow>
            </Grid>
          </Grid.Column>
        </StyledGrid>
      </StyledSegment>
    </HeaderField >
  }

  function getNaturalMin() {
    const otherMilk = watchOtherMilk ? parseInt(watchOtherMilk) : 0
    const milkSold = watchTotalAmount ? parseInt(watchTotalAmount) : 0
    return otherMilk + milkSold
  }

  function getNaturalMinWithDairyData() {
    const otherMilk = watchOtherMilk ? parseInt(watchOtherMilk) : 0
    const milkSold = options?.farmer?.totalMilkAmount ?? 0
    return otherMilk + milkSold
  }

  function renderBillingData() {
    return <HeaderField>
      <Header className='header' style={{ marginBottom: 5, display: 'flex', alignItems: 'center', gap: 10 }}>
        <img style={{ width: '2.5em' }} src={EuroCoin} />
        {t('Milk billing data')}
        <InfoPopup
          content={t('This data was pre-filled by the dairy and corresponds to the actual data for the survey year.')}
          styleIcon={{ fontSize: '1.3em' }}
          position={'top center'}
        />
      </Header>
      <CheckClimaEl.StyledDivider />
      {options?.farmer?.totalMilkAmount ? displayAsLabels() : renderInputs()}
      <CheckClimaEl.BillingWrapper>
        <Form.Group widths={'equal'} style={{ width: '100%', flexWrap: 'wrap', columnGap: '20px', rowGap: 10 }}>
          <FormFields.Input
            control={control}
            withoutAsterisk
            styleControlled={{ width: options?.useMilkPrice === 0 ? 250 : 200, marginLeft: 45 }}
            label={<div className='billing'>
              <img style={{ width: '2.5em' }} src={MilkProductIcon} />
              <div className='column-billing'>
                <div>{'Naturale Milchmenge (in kg)'}</div>
                <InfoPopup
                  content={'Die naturale Milchmenge umfasst die Summe der abgelieferten Milch und der Sperrmilch, der verfütterten Milch und der privat konsumierten Milch.'}
                  styleIcon={{ fontSize: '1.3em' }}
                  position='top center'
                />
                <LinkPopup
                  content={'LKV-Bericht'}
                />
              </div>
            </div>}
            pointingErrorLabel={'left'}
            styleErrorLabel={{ zIndex: 999, maxWidth: 300 }}
            name='naturalMilkAmount'
            noErrorMessage={!watchNaturalMilkAMount}
            placeholder={'kg/Jahr'}
            suffix={' kg'}
            typeInput={'controlled-error-input'}
            allowDecimals={false}
            rules={{
              required: true,
              min: {
                value: options?.farmer?.totalMilkAmount ? getNaturalMinWithDairyData() : getNaturalMin(),
                message: `Ihre naturale Milchmenge (${formatNumber(watchNaturalMilkAMount, 0)} kg) muss größer sein als ihre abgelieferte Milch (${formatNumber(getNaturalMin(), 0)} kg).
                Bitte geben Sie hier die Summe der abgelieferten Milch und der Sperrmilch, der verfütterten Milch und der privat konsumierten Milch an.`
              }
            }}
          />
        </Form.Group>
      </CheckClimaEl.BillingWrapper>
      <StyledOtherMilkWrapper>
        <Checkbox
          label={<label style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
            <div>{t('Erzeugen Sie weitere Milchmengen zur Direktvermarkung?')}</div>
            <InfoPopup
              content={t('This is needed to calculate the CO2 footprint of the total milk produced')}
              styleIcon={{ fontSize: '1.2em' }}
              position={'top center'}
            />
          </label>}
          checked={watchSellOtherMilkSelect}
          onChange={(_, data) => setValue('sellOtherMilkSelected', data.checked)}
        />
        {watchSellOtherMilkSelect && <FormFields.Input
          control={control}
          withoutAsterisk
          name='otherMilkAmount'
          placeholder={'kg/Jahr'}
          suffix={' kg'}
          styleControlled={{ width: 250, marginLeft: 25 }}
          typeInput={'controlled-error-input'}
          pointingErrorLabel={'left'}
          styleErrorLabel={{ zIndex: 999, maxWidth: 300, position: 'relative' }}
          allowDecimals={false}
          rules={{
            required: true,
            validate: (value) => {
              if (parseInt(value) > parseInt(watchNaturalMilkAMount)) {
                return `Ihre  Milchmenge zur Direktvermarktung (${formatNumber(value, 0)} kg) muss kleiner sein als Ihre naturale Milchmenge (${formatNumber(watchNaturalMilkAMount, 0)} kg).`
              }
              return true
            }
          }}
        />}
      </StyledOtherMilkWrapper>
    </HeaderField>
  }

  async function handleNoShowChecked() {
    try {
      await ClimaCheckService.disableFilePreview(id, { farmerId })
    } catch (error) {
      handleErrors(error)
    }
  }

  function renderPreviewModal() {
    return <PreviewModal
      isOpen={isPreviewModal}
      handleClose={(isNoShowChecked) => {
        setIsPreviewModal(false)
        setOptions({
          ...options,
          filePreview: !isNoShowChecked
        })
        if (isNoShowChecked) {
          handleNoShowChecked()
        }
      }}
    />
  }

  return <>
    {renderFarmerInfo()}
    {renderBillingData()}
    {renderPreviewModal()}
  </>
}
export default FirstStep